import React, { useState } from 'react';
import { useUserActions } from '@dopomogai/agent-kit';
import { Button, Tooltip, Icon, Spinner, Badge, Alert } from '@dopomogai/ui';
import type { UserDB } from '@dopomogai/types';
import { AddCreditsModal } from './AddCreditsModal';

interface UserListItemProps {
  user: UserDB;
  onActionSuccess: () => void;
}

export const UserListItem: React.FC<UserListItemProps> = ({ user, onActionSuccess }) => {
  const { addCredits, deleteUser, blockUser, unblockUser, isLoading, error } = useUserActions(onActionSuccess);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleAddCredits = async (amount: number, description: string) => {
    const success = await addCredits(user.id, { amount, description });
    if (success) {
      setIsModalOpen(false);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to PERMANENTLY DELETE user ${user.email}? This cannot be undone.`)) {
      deleteUser(user.id);
    }
  };

  const handleToggleBlock = () => {
    if (user.is_blocked) {
      unblockUser(user.id);
    } else {
      if (window.confirm(`Are you sure you want to block user ${user.email}? They will be logged out and unable to sign in.`)) {
        blockUser(user.id);
      }
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 items-center p-3 border-b border-slate-200 hover:bg-slate-50 text-sm">
        {/* Column 1: User Info & Block Status */}
        <div className="col-span-4 flex flex-col">
          <p className="font-semibold text-slate-800">{user.name || 'No Name'}</p>
          <p className="text-slate-500">{user.email}</p>
        </div>
        
        {/* Column 2: Role and Block Status Badge */}
        <div className="col-span-2 flex items-center gap-2">
            <Badge 
                text={user.role ?? 'user'} 
                accentColor={user.role === 'platform_admin' ? 'green' : 'gray'}
            />
            {/* --- NEW: Show Blocked Badge --- */}
            {user.is_blocked && (
                <Badge text="Blocked" accentColor="red" icon={<Icon name="ShieldAlert" />} />
            )}
        </div>
        
        {/* Column 3: Balance */}
        <div className="col-span-2 font-semibold text-blue-600">
          {/* --- FIX: Safely render the balance --- */}
          {typeof user.balance === 'number' ? `$${user.balance.toFixed(2)}` : '$0.00'}
        </div>

        {/* Column 4: Actions */}
        <div className="col-span-4 flex gap-2 justify-end">
          {isLoading ? <Spinner size="sm" /> : (
            <>
              <Tooltip text="Add Credits">
                <Button size="sm" variant="outline" icon={<Icon name="Plus" />} onClick={() => setIsModalOpen(true)} />
              </Tooltip>
              
              {/* --- FIX: Single, intelligent block/unblock button --- */}
              <Tooltip text={user.is_blocked ? 'Unblock User' : 'Block User'}>
                <Button 
                    size="sm" 
                    variant="outline" 
                    icon={user.is_blocked ? <Icon name="ShieldCheck" /> : <Icon name="ShieldOff" />} 
                    onClick={handleToggleBlock} 
                />
              </Tooltip>

              <Tooltip text="Delete User">
                <Button size="sm" variant="danger" icon={<Icon name="Trash2" />} onClick={handleDelete} />
              </Tooltip>
            </>
          )}
        </div>
      </div>
      
      {/* Conditionally render an error alert for this specific row's actions */}
      {error && <Alert type="error" message={error.message} className="mx-2 my-1" />}

      <AddCreditsModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleAddCredits}
        userName={user.name || user.email || ''}
      />
    </>
  );
};